import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';

/**
 * /comprar/:plan — crea el link de pago de Recurrente y redirige allí.
 * Protegida: el pago queda atado a quien está logueado (el webhook aplica el
 * plan a su cuenta u org). /pago/gracias es el regreso del checkout.
 */
export function ComprarPage() {
  useSurfaceTheme('bolsillo', 'light');
  const { plan } = useParams<{ plan: string }>();
  const [error, setError] = useState<string | null>(null);
  const lanzado = useRef(false);

  useEffect(() => {
    if (lanzado.current || !plan) return;
    lanzado.current = true;
    apiFetch<{ checkout_url: string }>('/pagos/checkout', {
      method: 'POST',
      json: { plan_codigo: plan },
    })
      .then((r) => {
        window.location.href = r.checkout_url;
      })
      .catch((e: { detail?: string }) =>
        setError(e?.detail ?? 'No se pudo crear el link de pago.'),
      );
  }, [plan]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-base px-6 text-center">
      {error ? (
        <>
          <AlertCircle className="h-10 w-10 text-error" aria-hidden="true" />
          <p className="max-w-md font-sans text-base text-content">{error}</p>
          <Link to="/#planes" className="font-sans text-sm text-primary underline">
            Volver a los planes
          </Link>
        </>
      ) : (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden="true" />
          <p className="font-sans text-base text-content">
            Preparando tu pago seguro con Recurrente…
          </p>
        </>
      )}
    </div>
  );
}

/**
 * /pago/gracias — el regreso del checkout. El plan lo aplica el webhook (a
 * veces tarda unos segundos): se pollea el estado del pago hasta verlo pagado.
 */
export function GraciasPage() {
  useSurfaceTheme('bolsillo', 'light');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pagoId = params.get('pago');
  const [estado, setEstado] = useState<'esperando' | 'pagado'>('esperando');

  // Plan confirmado → a trabajar. El pago pudo promover el rol (miembro →
  // facilitador) y hasta crear tenant propio: se refresca /me ANTES de
  // redirigir, para que /entrar decida con la cuenta ya actualizada.
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    if (estado !== 'pagado') return;
    const t = setTimeout(() => {
      void hydrate().finally(() => navigate('/entrar', { replace: true }));
    }, 2000);
    return () => clearTimeout(t);
  }, [estado, navigate, hydrate]);

  useEffect(() => {
    if (!pagoId) return;
    let intentos = 0;
    const timer = setInterval(() => {
      intentos += 1;
      void apiFetch<{ id: string; estado: string }[]>('/pagos/mios')
        .then((pagos) => {
          if (pagos.find((p) => p.id === pagoId && p.estado === 'pagado')) {
            setEstado('pagado');
            clearInterval(timer);
          }
        })
        .catch(() => undefined);
      if (intentos > 20) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [pagoId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-base px-6 text-center">
      <CheckCircle2 className="h-12 w-12 text-success" aria-hidden="true" />
      <h1 className="font-display text-2xl font-bold text-content">¡Gracias!</h1>
      <p className="max-w-md font-sans text-base text-content-muted">
        {estado === 'pagado'
          ? 'Tu plan ya está activo: te llevamos a tus procesos…'
          : 'Recibimos tu pago y estamos activando tu plan — toma unos segundos.'}
      </p>
      <Link
        to="/entrar"
        className="mt-2 inline-flex min-h-touch items-center justify-center rounded-xl bg-primary px-6 py-3 font-sans text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
      >
        Ir a mi cuenta
      </Link>
    </div>
  );
}
